import { styled } from '@linaria/react';
import { Alert } from '@mantine/core';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useFloraPageViewUrl } from '../../../api/flora-pages-client/flora-pages-client.hooks';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';

interface FloraPageEmbedHandle {
  /** Mint a fresh signed URL and remount the iframe (used by the viewer's reload button). */
  reload: () => void;
}

interface FloraPageEmbedProps {
  pageId: string | undefined;
}

/**
 * Renders a published Flora page inside a sandboxed iframe, filling its
 * container. Shared by the full-window page viewer route and the Flora-agent
 * chat rail so the security boundary and the signed-URL lifecycle live in one
 * place.
 *
 * The AI-generated HTML is untrusted, so it renders with
 * `sandbox="allow-scripts"` and WITHOUT `allow-same-origin`: the document gets
 * an opaque origin with no access to the app's cookies, storage, or DOM. The
 * `src` is a short-lived signed URL minted per mount; `reload()` refetches a
 * fresh one (and remounts the frame via its `key`) when the token expires.
 *
 * @param {FloraPageEmbedProps} props - The page to embed
 * @param {React.Ref<FloraPageEmbedHandle>} ref - Imperative handle exposing `reload`
 * @returns {JSX.Element} The embedded page (loading overlay / error alert / iframe)
 */
const FloraPageEmbed = forwardRef<FloraPageEmbedHandle, FloraPageEmbedProps>(function FloraPageEmbed({ pageId }, ref) {
  const { viewUrl, query } = useFloraPageViewUrl(pageId);

  useImperativeHandle(ref, () => ({ reload: () => query.refetch() }), [query]);

  // Keep the overlay up through BOTH phases: minting the signed URL
  // (`query.isLoading`) AND the iframe fetching/rendering its own document —
  // otherwise the user stares at a blank white frame for a large page. Track
  // WHICH url has finished loading (rather than a boolean reset by an effect):
  // when the url changes on a reload or a page switch, `loadedUrl` no longer
  // matches, so the overlay returns until the fresh iframe fires `load`.
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const frameReady = !!viewUrl && loadedUrl === viewUrl.url;
  const overlayVisible = query.isLoading || (!!viewUrl && !query.isError && !frameReady);

  return (
    <EmbedRoot>
      <BrandedLoadingOverlay scoped visible={overlayVisible} transitionDuration={30} variant="colored" />
      {query.isError && (
        <ErrorWrapper>
          <Alert color="red" title="Page unavailable">
            This page could not be loaded — it may have been deleted or unshared.
          </Alert>
        </ErrorWrapper>
      )}
      {viewUrl && !query.isError && (
        <PageFrame
          key={viewUrl.url}
          src={viewUrl.url}
          title="Published page"
          // Security boundary: allow-scripts WITHOUT allow-same-origin gives the
          // untrusted document an opaque origin. Do not add sandbox capabilities
          // without a security review.
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
          data-testid="flora-page-iframe"
          onLoad={() => setLoadedUrl(viewUrl.url)}
        />
      )}
    </EmbedRoot>
  );
});

const EmbedRoot = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: var(--base-white);
`;

const PageFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
`;

const ErrorWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 96px;
`;

export { FloraPageEmbed };
export type { FloraPageEmbedHandle };
